<!--
  HospitalPickerTable.vue
  내선 관리 진입 화면 — 병원 목록(검색·페이지네이션)에서 행을 눌러 대상 병원을 고른다.

  상담톡 관리(CounselorManage)와 같은 "목록 먼저, 검색은 위에" 구조다. 검색 카테고리·검색어·페이지는
  부모가 라우트 쿼리로 들고 있어(뒤로 가기·딥링크 유지) 이 컴포넌트는 표시와 이벤트만 맡는다.

  mode 두 가지(부모가 정한다):
  - organizations: call-api 콜봇 조직 목록(콜봇을 쓰는 병원만). 열: 병원명·조직 키·인증·만든 날.
    검색은 이름만, 페이지는 부모가 화면에서 나눈다.
  - hospitals: 조직 목록 라우트가 미배포일 때의 폴백 — inapi 전체 병원(POST /companies/list/v2).
    열: 병원명·사업자등록번호·지역. 검색 카테고리 선택이 붙는다.

  - 행 클릭 → emit('select', record) — record._id 가 call-api 경로의 companyId 다.
  - 검색 → emit('search', { search, condition }) / 페이지·페이지 크기 → emit('page', { page, pageSize })
-->
<template>
  <div class="hospital-picker">
    <a-alert v-if="notice" type="warning" show-icon :message="notice" class="hospital-picker__notice" />

    <div class="hospital-picker__filter">
      <div class="hospital-picker__filter-item">
        <label>검색</label>
        <a-select
          v-if="mode === 'hospitals'"
          v-model:value="localSearch"
          :options="SEARCH_OPTIONS"
          style="width: 150px"
        />
        <a-input
          v-model:value="localCondition"
          :placeholder="conditionPlaceholder"
          allow-clear
          style="width: 260px"
          @pressEnter="submitSearch"
        />
      </div>
      <div class="hospital-picker__filter-actions">
        <a-button type="primary" class="yellow-button" :loading="loading" @click="submitSearch">검색</a-button>
        <a-button v-if="isFiltered" @click="clearSearch">전체 보기</a-button>
      </div>
    </div>

    <p class="hospital-picker__hint">
      {{ mode === 'organizations' ? '콜봇 조직이 있는 병원만 보입니다. ' : '' }}병원을 누르면 그 병원의 내선·전화기·설치 장소 관리로 들어갑니다.
    </p>

    <a-table
      :columns="columns"
      :data-source="rows"
      :loading="loading"
      :pagination="paginationConfig"
      :row-key="(record) => record._id"
      :custom-row="customRow"
      size="middle"
      @change="onTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'name'">
          <span class="hospital-picker__name" :class="{ 'is-missing': record.nameMissing }">{{ record.name }}</span>
          <span v-if="record.nameMissing" class="hospital-picker__sub">회사 정보 없음</span>
        </template>
        <template v-else-if="column.key === 'keyStatus'">
          <a-tag :color="record.keyStatus === 'active' ? 'green' : 'orange'">
            {{ record.keyStatus === 'active' ? '사용 중' : '키 발급 대기' }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'verification'">
          <a-tag v-if="verificationLabel(record.verificationStatus)" :color="verificationColor(record.verificationStatus)">
            {{ verificationLabel(record.verificationStatus) }}
          </a-tag>
          <span v-else>—</span>
        </template>
        <template v-else-if="column.key === 'createdAt'">
          {{ record.createdAt ? formatPbxTime(record.createdAt) : '—' }}
        </template>
        <template v-else-if="column.key === 'cNo'">
          {{ formatBusinessNumber(record.cNo) }}
        </template>
        <template v-else-if="column.key === 'address'">
          {{ formatAddress(record) }}
        </template>
        <template v-else-if="column.key === 'action'">
          <a-button type="link" size="small" @click.stop="$emit('select', record)">내선 관리</a-button>
        </template>
      </template>
      <template #emptyText>
        <a-empty :description="isFiltered ? '검색 결과가 없습니다.' : mode === 'organizations' ? '콜봇 조직이 있는 병원이 없습니다. 병원이 studio 에서 콜봇 조직을 만들면 여기에 나타납니다.' : '등록된 병원이 없습니다.'" />
      </template>
    </a-table>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { formatPbxTime } from '@/views/Callbot/service/pbxExtensions';

const props = defineProps({
  /** organizations = call-api 콜봇 조직 목록 / hospitals = inapi 전체 병원(폴백) */
  mode: { type: String, default: 'organizations' },
  /** 상단 경고(폴백 중 안내 등). 비어 있으면 숨긴다 */
  notice: { type: String, default: '' },
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  /** 현재 검색 카테고리(name | cNo | adminName) — 라우트 쿼리 원천 */
  search: { type: String, default: 'name' },
  /** 현재 검색어 — 라우트 쿼리 원천 */
  condition: { type: String, default: '' },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  total: { type: Number, default: 0 },
});

const emit = defineEmits(['select', 'search', 'page']);

const SEARCH_OPTIONS = [
  { value: 'name', label: '병원명' },
  { value: 'cNo', label: '사업자등록번호' },
  { value: 'adminName', label: '관리자명' },
];

// 입력 중인 값은 로컬에 두고, [검색]·Enter 때만 부모(라우트 쿼리)로 올린다.
// 타이핑마다 URL 이 바뀌면 뒤로 가기 이력이 글자 수만큼 쌓인다.
const localSearch = ref(props.search);
const localCondition = ref(props.condition);
watch(
  () => props.search,
  (v) => {
    localSearch.value = v;
  }
);
watch(
  () => props.condition,
  (v) => {
    localCondition.value = v;
  }
);

const conditionPlaceholder = computed(() => {
  const found = SEARCH_OPTIONS.find((o) => o.value === localSearch.value);
  return `${found ? found.label : '검색어'}으로 검색`;
});

const isFiltered = computed(() => Boolean(props.condition));

function submitSearch() {
  emit('search', { search: localSearch.value, condition: (localCondition.value || '').trim() });
}

function clearSearch() {
  localCondition.value = '';
  emit('search', { search: localSearch.value, condition: '' });
}

const ORGANIZATION_COLUMNS = [
  { key: 'index', title: '번호', dataIndex: 'index', align: 'center', width: 72 },
  { key: 'name', title: '병원명', dataIndex: 'name' },
  { key: 'keyStatus', title: '조직 키', dataIndex: 'keyStatus', align: 'center', width: 130 },
  { key: 'verification', title: '인증', dataIndex: 'verificationStatus', align: 'center', width: 110 },
  { key: 'createdAt', title: '조직 생성', dataIndex: 'createdAt', align: 'center', width: 180 },
  { key: 'action', title: '', dataIndex: 'action', align: 'center', width: 100 },
];

// 관리자 열은 두지 않는다 — list/v2 응답(docs)에 관리자 이름이 없다(검색 카테고리 adminName 은 서버가 계정 컬렉션을 조인해 처리하므로 검색은 된다)
const HOSPITAL_COLUMNS = [
  { key: 'index', title: '번호', dataIndex: 'index', align: 'center', width: 72 },
  { key: 'name', title: '병원명', dataIndex: 'name' },
  { key: 'cNo', title: '사업자등록번호', dataIndex: 'cNo', align: 'center', width: 160 },
  { key: 'address', title: '지역', dataIndex: 'addrOne', align: 'center' },
  { key: 'action', title: '', dataIndex: 'action', align: 'center', width: 100 },
];

const columns = computed(() => (props.mode === 'organizations' ? ORGANIZATION_COLUMNS : HOSPITAL_COLUMNS));

/** vox 조직 인증(KYC) 상태 미러 — 라벨은 studio 조직 인증 화면과 같은 말을 쓴다 */
const VERIFICATION_LABELS = {
  approved: ['승인', 'green'],
  requested: ['심사 중', 'blue'],
  pending: ['심사 중', 'blue'],
  rejected: ['거절', 'red'],
  revoked: ['취소', 'default'],
};
const verificationLabel = (status) => (status && VERIFICATION_LABELS[status] ? VERIFICATION_LABELS[status][0] : '');
const verificationColor = (status) => (status && VERIFICATION_LABELS[status] ? VERIFICATION_LABELS[status][1] : 'default');

const paginationConfig = computed(() => ({
  current: props.page,
  pageSize: props.pageSize,
  total: props.total,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50'],
  showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
}));

function onTableChange(pagination) {
  emit('page', { page: pagination.current, pageSize: pagination.pageSize });
}

const customRow = (record) => ({
  onClick: () => emit('select', record),
  style: { cursor: 'pointer' },
});

/** 1234567890 → 123-45-67890. 형식이 다르면 그대로 */
function formatBusinessNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) return value || '-';
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function formatAddress(record) {
  const parts = [record.addrOne, record.addrTwo, record.addrThree].filter(Boolean);
  return parts.length ? parts.join(' ') : '-';
}
</script>

<style scoped lang="scss">
.hospital-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px;
    background: #fafafa;
    border-radius: 8px;
  }

  &__filter-item {
    display: flex;
    align-items: center;
    gap: 8px;

    label {
      font-size: 13px;
      font-weight: 600;
      color: #262626;
      margin: 0;
    }
  }

  &__filter-actions {
    display: flex;
    gap: 8px;
  }

  &__hint {
    margin: 0;
    font-size: 13px;
    color: #8c8c8c;
  }

  &__notice {
    margin: 0;
  }

  &__name {
    font-weight: 600;
    color: #262626;

    &.is-missing {
      font-weight: 400;
      color: #8c8c8c;
      font-family: monospace;
    }
  }

  &__sub {
    margin-left: 8px;
    font-size: 12px;
    color: #8c8c8c;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }
}
</style>
